import {
    CoachSmartCatalogPayload,
    SmartCatalogFundingOrganismOption,
    SmartCatalogModality,
    SmartCatalogOptions,
    SmartCatalogPersonalFunding,
    SmartCatalogProduct,
    SmartCatalogProductNextSession,
    SmartCatalogTheme,
    SmartCatalogTrigger
} from "@/services/walter-assistant";

/**
 * Seuils d'affichage des badges de disponibilité. Doivent rester alignés
 * avec les constantes Django côté backend (apps/smart_catalog) :
 *
 * - `SMART_CATALOG_SEATS_BADGE_THRESHOLD` (settings/base.py) → seuil à
 *   partir duquel le badge "X places restantes" s'affiche en rouge
 *   tempéré sur une ligne synchrone.
 * - `SMART_CATALOG_NEXT_SESSION_FAR_DAYS` (settings/base.py) → délai
 *   au-delà duquel la prochaine session est signalée comme "lointaine"
 *   ("Prochaine session le JJ/MM").
 *
 * Le backend ne propage pas ces seuils au front via une API : si ops
 * les bouge côté Django sans répliquer ici, on aura un drift visuel.
 * Pas de mécanisme runtime pour résoudre ça côté MVP — un commentaire
 * de garde et un test de présence suffisent.
 */
export const SEATS_BADGE_THRESHOLD = 5;
export const NEXT_SESSION_FAR_DAYS = 30;

/**
 * Nombre de produits mis en avant par thème. Au-delà de ce rang, les
 * produits sont rendus grisés (présents mais non mis en avant — spec
 * Section 3). Doit rester aligné avec ``DISPLAY_TOP_N_PER_THEME`` côté
 * backend (regeneration_orchestrator).
 */
export const DISPLAY_TOP_N_PER_THEME = 4;

/** Nombre de produits visibles par thème avant expansion « Voir plus ». */
export const DISPLAY_INITIAL_N_PER_THEME = 1;

/** Nombre de thèmes visibles par sous-catalogue avant expansion « Voir plus ». */
export const DISPLAY_INITIAL_N_THEMES_PER_GROUP = 3;

/** Nombre de thèmes supplémentaires révélés à chaque clic sur « Voir plus ». */
export const DISPLAY_NEXT_N_THEMES_PER_GROUP = DISPLAY_INITIAL_N_THEMES_PER_GROUP;

/**
 * Seuil minimal d'avis avant affichage de la note. Aligné avec
 * `SMART_CATALOG_REVIEWS_MIN_COUNT` côté backend (scorer).
 */
export const REVIEWS_MIN_COUNT = 20;

/** True when a product rating should be surfaced in the UI. */
export function shouldShowProductRating(avg: number | null | undefined, count: number | null | undefined): boolean {
    if (avg == null || count == null || Number.isNaN(avg) || Number.isNaN(count)) return false;
    return count >= REVIEWS_MIN_COUNT;
}

/** Libellé « 4,5/5 (198) » pour la note produit. */
export function formatProductRatingLabel(avg: number, count: number): string {
    const formattedAvg = avg.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const formattedCount = count.toLocaleString("fr-FR");
    return `${formattedAvg}/5 (${formattedCount})`;
}

/**
 * Formatte un prix en euros avec séparateur français (espace fine
 * insécable conventionnelle remplacée ici par un espace simple pour
 * éviter les soucis de copie-collé côté commercial). `null` → tiret
 * cadratin pour signaler l'absence sans laisser la cellule vide.
 */
export function formatPriceEUR(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return "—";
    const rounded = Math.round(value);
    /*
     * `toLocaleString("fr-FR")` insère un narrow no-break space (U+202F)
     * comme séparateur de milliers. C'est sémantiquement correct mais
     * pénible en copie-collé (le commercial peut vouloir reprendre
     * "1 234 €" tel quel dans un email). On remplace par un espace
     * simple — cohérent avec le brief ("X XXX €") et plus robuste.
     */
    return `${rounded.toLocaleString("fr-FR").replace(/\u202f|\u00a0/g, " ")} €`;
}

/**
 * Libellé court du montant personnel pour la colonne Montant de la
 * matrice produit.
 *
 * - Fonds propres → "Fonds propres : 1 499 €"
 * - RAC exact ou estimé (montant seul) → "RAC : 99 €"
 * - RAC fourchette (OPCO sans funding_price négocié, issu du FundingRate
 *   théorique) → "RAC : entre 100 € et 400 €"
 * - Inconnu → tiret cadratin.
 *
 * N'inclut pas l'astérisque d'estimation (``exact=false``) — c'est au
 * composant de rendre le tooltip associé.
 */
export function formatPersonalFunding(pf: SmartCatalogPersonalFunding | null | undefined): string {
    if (!pf) return "—";
    const label = pf.type === "fp" ? "Fonds propres" : "RAC";

    // Fourchette RAC (OPCO sans prise en charge négociée).
    if (pf.type === "rac" && pf.montant == null && pf.montant_min != null && pf.montant_max != null) {
        return `${label}\u00a0: entre ${formatPriceEUR(pf.montant_min)} et ${formatPriceEUR(pf.montant_max)}`;
    }

    const montant = pf.montant != null ? formatPriceEUR(pf.montant) : "—";
    return `${label}\u00a0: ${montant}`;
}

/**
 * Durée d'une formation en heures (ex: `21h`). On affiche un entier si
 * l'arrondi est strictement entier, sinon une décimale (`1.5h`). Les
 * durées du catalogue sont en heures entières en pratique mais le
 * champ backend est nullable / typé `float`.
 */
export function formatDurationHours(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) return "—";
    const rounded = Math.round(value * 10) / 10;
    if (Number.isInteger(rounded)) return `${rounded}h`;
    return `${rounded.toString().replace(".", ",")}h`;
}

/**
 * Date courte FR (`15/12`) pour le badge "Prochaine session le JJ/MM".
 * `null` → chaîne vide (l'appelant doit cacher le badge).
 */
export function formatShortDateFR(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
}

/** Couleur neutre pour les financeurs inconnus ou absents. */
export const FUNDING_ORGANISM_FALLBACK_COLOR = "#9CA3AF";

export type FundingOrganismBrand = {
    name: string;
    color: string;
};

const FUNDING_ORGANISM_BRANDS: FundingOrganismBrand[] = [
    { name: "FIFPL", color: "#0B5394" },
    { name: "ANDPC", color: "#E8730C" },
    { name: "CPF", color: "#0D2C7A" },
    { name: "Alma", color: "#00B894" },
    { name: "GoCardless", color: "#F1F252" },
    { name: "FAFPM", color: "#1D6FB8" },
    { name: "OPCO EP", color: "#E2001A" },
    { name: "Ocapiat", color: "#6AB023" },
    { name: "ATLAS", color: "#1B3A6B" },
    { name: "Uniformation", color: "#E6007E" },
    { name: "AKTO", color: "#FF6B00" },
    { name: "OPCO Mobilités", color: "#0099D8" },
    { name: "OPCO Santé", color: "#00A7A0" },
    { name: "AFDAS", color: "#E2001A" },
    { name: "OPCO 2i", color: "#003D7C" },
    { name: "Stripe", color: "#635BFF" },
    { name: "Virement bancaire", color: "#607D8B" },
    { name: "AGEFICE", color: "#004B87" },
    { name: "Opcommerce", color: "#E2001A" },
    { name: "Formation offerte", color: "#9C27B0" },
    { name: "ANFH", color: "#0072BC" },
    { name: "Kairos (France Travail)", color: "#E1000F" },
    { name: "FAFCEA", color: "#2E5C8A" },
    { name: "CNFPT", color: "#0055A4" },
    { name: "IPERIA", color: "#E94E1B" }
];

const FUNDING_ORGANISM_ALIASES: Record<string, string> = {
    DPC: "ANDPC",
    "FIF-PL": "FIFPL",
    FIFPL: "FIFPL",
    OPCO_EP: "OPCO EP",
    OPCOEP: "OPCO EP",
    OPCO_MOBILITES: "OPCO Mobilités",
    OPCOMOBILITES: "OPCO Mobilités",
    OPCO_SANTE: "OPCO Santé",
    OPCOSANTE: "OPCO Santé",
    OPCO_2I: "OPCO 2i",
    OPCO2I: "OPCO 2i",
    ALMA: "Alma",
    GOCARDLESS: "GoCardless",
    STRIPE: "Stripe",
    VIREMENT: "Virement bancaire",
    VIREMENT_BANCAIRE: "Virement bancaire",
    FORMATION_OFFERTE: "Formation offerte",
    KAIROS: "Kairos (France Travail)",
    FRANCE_TRAVAIL: "Kairos (France Travail)"
};

/** Normalise un code financeur pour le lookup (insensible à la casse, aux séparateurs et aux accents). */
export function normalizeFundingKey(code: string): string {
    return code
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, "");
}

const FUNDING_ORGANISM_BY_KEY = (() => {
    const byKey = new Map<string, FundingOrganismBrand>();

    const register = (brand: FundingOrganismBrand, aliases: string[] = []) => {
        for (const alias of [brand.name, ...aliases]) {
            byKey.set(normalizeFundingKey(alias), brand);
        }
    };

    for (const brand of FUNDING_ORGANISM_BRANDS) {
        register(brand);
    }
    for (const [alias, canonicalName] of Object.entries(FUNDING_ORGANISM_ALIASES)) {
        const brand = FUNDING_ORGANISM_BRANDS.find(item => item.name === canonicalName);
        if (brand) {
            register(brand, [alias]);
        }
    }

    return byKey;
})();

/** Résout le libellé et la couleur officielle d'un financeur à partir de son code CRM. */
export function resolveFundingBrand(code: string | null | undefined): FundingOrganismBrand {
    const trimmed = (code ?? "").trim();
    if (!trimmed) {
        return { name: "Fonds propres", color: FUNDING_ORGANISM_FALLBACK_COLOR };
    }

    return (
        FUNDING_ORGANISM_BY_KEY.get(normalizeFundingKey(trimmed)) ?? {
            name: trimmed,
            color: FUNDING_ORGANISM_FALLBACK_COLOR
        }
    );
}

/** Couleur de texte contrastée pour un badge sur fond brandé. */
export function getFundingBadgeTextColor(backgroundColor: string): string {
    const hex = backgroundColor.replace("#", "");
    if (hex.length !== 6) return "#ffffff";

    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.62 ? "#1f2937" : "#ffffff";
}

/** Libellé humain pour la chip de financement. */
export function formatFundingLabel(code: string | null | undefined): string {
    return resolveFundingBrand(code).name;
}

/**
 * Humanise un code profession (slug `Profession.code` côté CRM, ex.
 * `infirmier_liberal`) en un libellé affichable. Heuristique simple :
 * on remplace `_` par des espaces et on capitalise la première lettre.
 *
 * À termes on pourra remplacer cette fonction par un lookup contre
 * `/registration/content/professions/` (cf. router walter-crm) pour
 * obtenir le libellé canonique entretenu côté backend ; pour l'instant
 * la version heuristique évite un fetch supplémentaire à l'ouverture
 * de la modale et reste lisible en pratique.
 */
export function formatProfessionLabel(code: string | null | undefined): string {
    const trimmed = (code ?? "").trim();
    if (!trimmed) return "";
    const cleaned = trimmed.replace(/[_-]+/g, " ");
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export type ModalityDisplayKind = "synchrone" | "asynchrone" | "hybride" | "unknown";

export type ModalityDisplay = {
    label: string;
    kind: ModalityDisplayKind;
};

/** Libellé et type normalisé pour la chip de modalité. */
export function resolveModalityDisplay(modality: SmartCatalogModality): ModalityDisplay {
    const value = (modality || "").toLowerCase();
    switch (value) {
        case "synchrone":
            return { label: "100% CV", kind: "synchrone" };
        case "asynchrone":
            return { label: "Asynchrone", kind: "asynchrone" };
        case "hybride":
            return { label: "Mixte", kind: "hybride" };
        default:
            return { label: modality || "—", kind: "unknown" };
    }
}

/** Libellé humain pour la chip de modalité — gère les casses mixtes. */
export function formatModalityLabel(modality: SmartCatalogModality): string {
    return resolveModalityDisplay(modality).label;
}

/**
 * "Mis à jour il y a Xs" / "il y a X min". Référence wall-clock locale
 * (le payload porte `generated_at_ms` en wall-clock ms).
 */
export function formatRelativeFreshness(generatedAtMs: number, nowMs: number = Date.now()): string {
    const diffSec = Math.max(0, Math.floor((nowMs - generatedAtMs) / 1000));
    if (diffSec < 5) return "À l'instant";
    if (diffSec < 60) return `Il y a ${diffSec}s`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    const diffHour = Math.floor(diffMin / 60);
    return `Il y a ${diffHour}h`;
}

const TRIGGER_LABELS: Record<SmartCatalogTrigger, string> = {
    initial: "Catalogue initial",
    signal_change: "Mis à jour suite à un changement de profil",
    manual: "Mise à jour manuelle",
    manual_override: "Mis à jour après un filtre manuel"
};

export function formatTriggerLabel(trigger: SmartCatalogTrigger): string {
    return TRIGGER_LABELS[trigger] ?? "Mise à jour";
}

/**
 * Devrait-on afficher le badge "X places restantes" pour cette
 * session ? Vrai uniquement si la modalité est synchrone, qu'on a un
 * `seats_left` numérique et qu'il est sous le seuil. Le backend filtre
 * déjà sur la disponibilité, ici on ne fait que de l'affichage.
 */
export function shouldShowSeatsBadge(product: SmartCatalogProduct): boolean {
    const modality = (product.modality || "").toLowerCase();
    if (modality !== "synchrone") return false;
    const session = (product.next_sessions ?? [])[0];
    if (!session || session.seats_left == null) return false;
    return session.seats_left > 0 && session.seats_left <= SEATS_BADGE_THRESHOLD;
}

/**
 * Devrait-on afficher le badge "Prochaine session le JJ/MM" ? Vrai
 * uniquement pour les synchrones dont la prochaine session est au-delà
 * du seuil de jours, et dont la date est dans le futur. Robuste aux
 * dates passées (qu'on évite d'afficher en bandeau "future").
 */
export function shouldShowFarSessionBadge(product: SmartCatalogProduct, nowMs: number = Date.now()): boolean {
    const modality = (product.modality || "").toLowerCase();
    if (modality !== "synchrone") return false;
    const session = (product.next_sessions ?? [])[0];
    if (!session?.starts_at) return false;
    const startsAt = new Date(session.starts_at).getTime();
    if (Number.isNaN(startsAt)) return false;
    const diffDays = (startsAt - nowMs) / (1000 * 60 * 60 * 24);
    return diffDays > NEXT_SESSION_FAR_DAYS;
}

/** Libellé du badge "X places restantes" — appelant garantit le filtre `shouldShowSeatsBadge`. */
export function formatSeatsBadgeLabel(session: SmartCatalogProductNextSession): string {
    const left = session.seats_left ?? 0;
    return left <= 1 ? `${left} place restante` : `${left} places restantes`;
}

/** Catalogue vide = aucun thème porteur d'au moins un produit. */
export function isPayloadEmpty(payload: CoachSmartCatalogPayload | null): boolean {
    if (!payload) return true;
    const themes = payload.themes ?? [];
    return themes.every(theme => (theme.products?.length ?? 0) === 0);
}

export const SMART_CATALOG_SUB_TABLE_LABELS: Record<string, string> = {
    sante: "Santé",
    generaliste: "Généraliste",
    edumiam: "Edumiam",
    alternance: "Alternance"
};

export type SmartCatalogSubTableGroup = {
    subTable: string;
    label: string;
    themes: SmartCatalogTheme[];
};

const FORMATION_RESOURCES_BASE_URL = "https://walter-resources.s3.amazonaws.com/formations";

/** Normalise un code produit CRM (retire les suffixes `#pc…` et `:ide:…`). */
export function normalizeFormationProductCode(productCode: string | null | undefined): string {
    return (productCode ?? "").trim().split("#")[0].split(":")[0];
}

function getFormationAssetUrl(productCode: string | null | undefined, filename: string): string | null {
    const code = normalizeFormationProductCode(productCode);
    if (!code) return null;
    return `${FORMATION_RESOURCES_BASE_URL}/${encodeURIComponent(code)}/img2/${filename}`;
}

/** URL de la bannière Walter Resources pour un code produit. */
export function getFormationBannerUrl(productCode: string | null | undefined): string | null {
    return getFormationAssetUrl(productCode, "banner_w840_h473.jpeg");
}

/** URL du logotype Walter Resources pour un code produit. */
export function getFormationLogotypeUrl(productCode: string | null | undefined): string | null {
    return getFormationAssetUrl(productCode, "logotype.svg");
}

/** Produit représentatif d'un sous-catalogue (meilleur thème → meilleur produit). */
export function getSubTableGroupLogotypeProductCode(group: SmartCatalogSubTableGroup): string | null {
    const topTheme = group.themes[0];
    return topTheme?.products?.[0]?.product_code ?? null;
}

/** Distinct sub-table identifiers across themes that carry products. */
export function getDistinctSubTables(themes: SmartCatalogTheme[]): string[] {
    const seen = new Set<string>();
    for (const theme of themes) {
        if ((theme.products?.length ?? 0) === 0) continue;
        seen.add(theme.sub_table || "unknown");
    }
    return [...seen];
}

/** True when themes span several sub-tables and should be split into catalog tabs. */
export function shouldGroupThemesBySubTable(themes: SmartCatalogTheme[]): boolean {
    return getDistinctSubTables(themes).length > 1;
}

/**
 * Groups themes by sub-table, ordered according to ``catalogOrigins``.
 * Empty groups are omitted.
 */
export function groupThemesBySubTable(themes: SmartCatalogTheme[], catalogOrigins: string[]): SmartCatalogSubTableGroup[] {
    const order: string[] = catalogOrigins.length > 0 ? catalogOrigins : ["sante", "generaliste"];
    const bySubTable = new Map<string, SmartCatalogTheme[]>();

    for (const theme of themes) {
        if ((theme.products?.length ?? 0) === 0) continue;
        const subTable = theme.sub_table || "unknown";
        const bucket = bySubTable.get(subTable) ?? [];
        bucket.push(theme);
        bySubTable.set(subTable, bucket);
    }

    const groups: SmartCatalogSubTableGroup[] = [];
    for (const subTable of order) {
        const bucket = bySubTable.get(subTable);
        if (!bucket?.length) continue;
        groups.push({
            subTable,
            label: SMART_CATALOG_SUB_TABLE_LABELS[subTable] ?? subTable,
            themes: [...bucket].sort((a, b) => b.score - a.score)
        });
    }

    for (const [subTable, bucket] of bySubTable.entries()) {
        if (order.includes(subTable) || !bucket.length) continue;
        groups.push({
            subTable,
            label: SMART_CATALOG_SUB_TABLE_LABELS[subTable] ?? subTable,
            themes: [...bucket].sort((a, b) => b.score - a.score)
        });
    }

    return groups;
}

/**
 * Restreint les options du sélecteur financeur en fonction du statut
 * professionnel courant (UX du Plan 3 : on ne propose que les financeurs
 * réellement mobilisables par le statut).
 *
 * Règles :
 * - Pas d'options chargées ⇒ liste vide (le sélecteur ne s'affiche pas).
 * - Statut absent OU statut dont le mapping est vide/inconnu ⇒ **aucune
 *   contrainte**, on renvoie tous les financeurs. Un statut sans
 *   financeur explicite ne doit pas produire un sélecteur mort : l'agent
 *   garde la main (l'override est de toute façon un choix délibéré).
 * - Statut avec mapping non vide ⇒ on ne garde que les financeurs
 *   éligibles, en **préservant toujours le financeur courant** même hors
 *   scope (sinon un override existant afficherait une valeur vide).
 */
export function scopeFundingOptions(options: SmartCatalogOptions | null, statusCode: string | null | undefined, currentCode?: string | null): SmartCatalogFundingOrganismOption[] {
    if (!options) return [];
    const all = options.funding_organisms ?? [];
    const status = (statusCode ?? "").trim();
    const allowed = status ? options.status_to_fundings?.[status] : undefined;
    if (!allowed || allowed.length === 0) {
        return all;
    }
    const allowedSet = new Set(allowed);
    const current = (currentCode ?? "").trim();
    if (current) allowedSet.add(current);
    return all.filter(fo => allowedSet.has(fo.code));
}
